/**
 * ItemCard - Componente UNIFICATO per tutti i tipi di item (Barche, Motori, Accessori)
 * REFACTORED: Stile nativo iOS con più informazioni per cantiere nautico
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Navigation, Ruler, Zap, Package, Calendar, Users, Star } from 'lucide-react';
import MarketPrice from './MarketPrice';
import { RulerLucideIcon } from './Icons';
import { ItemType, getItemConfig } from '../config/itemConfig';
import { fixImageUrl, getWebPUrl } from '../utils/images';
import { useGlassmorphism } from '../hooks/useGlassmorphism';
import { useThemeStyled } from '../hooks/useThemeStyled';
import { useIsAuthenticated } from '../stores/auth';
import { getPermissionSettings } from '../utils/permissions';
import { formatAccessoryType } from '../config/accessoryTypeSpecs';

function OptimizedImage({ src, alt, className, width, height, sizes, onLoad, onError, eager }: { src: string, alt: string, className?: string, width?: number, height?: number, sizes?: string, onLoad?: () => void, onError?: () => void, eager?: boolean }) {
  const webpSrc = getWebPUrl(src);
  const [imgSrc, setImgSrc] = React.useState(webpSrc || src);

  const handleError = () => {
    if (imgSrc !== src) {
      setImgSrc(src);
    }
    onError?.();
  };

  return (
    <img
      src={imgSrc}
      alt={alt}
      className={className}
      width={width}
      height={height}
      sizes={sizes}
      loading={eager ? 'eager' : 'lazy'}
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      {...(eager ? ({ fetchPriority: 'high' } as any) : {})}
      decoding="async"
      onLoad={onLoad}
      onError={handleError}
    />
  );
}

interface ItemCardProps {
  item: any;
  itemType: ItemType;
  compact?: boolean;
  gridMode?: boolean;
  distance?: number;
  /** Prime card visibili above-the-fold: immagine principale caricata subito (LCP) */
  priority?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  canReserve?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  canChat?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  canMakeOffer?: boolean;
}

function formatPrice(price?: number): string {
  if (!price || price === 0) return 'Contatta il dealer';
  return `€ ${price.toLocaleString('it-IT')}`;
}

// Calcola la percentuale di guadagno
function calculateProfitPercentage(dealerPrice?: number, retailPrice?: number): number | null {
  if (!dealerPrice || !retailPrice || retailPrice <= dealerPrice) return null;
  return Math.round(((retailPrice - dealerPrice) / dealerPrice) * 100);
}

// Colore basato sulla distanza - usa CSS variable del template
function getDistanceBadgeColor(_distance: number): string {
  // Usa il colore primary del template
  return 'var(--color-primary)';
}

function getItemIcon(itemType: ItemType) {
  switch (itemType) {
    case 'boats': return Ruler;
    case 'motors': return Zap;
    case 'accessories': return Package;
    default: return Ruler;
  }
}

// Helper per formattare il tipo barca - USA boat_type_code
function formatBoatType(typeCode: string, fallbackType: string): string {
  if (!typeCode) return fallbackType || "";

  const typeNames: Record<string, string> = {
    GOM: "Gommone",
    RIB: "RIB",
    TEN: "Tender",
    OPN: "Open",
    CC: "Center Console",
    WLK: "Walkaround",
    FLY: "Flybridge",
    SPT: "Sport",
    FSH: "Fishing",
    CAT: "Catamarano",
    MON: "Vela",
    MYT: "Yacht",
  };

  return typeNames[typeCode.toUpperCase()] || fallbackType || typeCode;
}

// Helper per formattare motore
function formatEngineInfo(item: any): string {
  if (item.engines_brand) {
    const hp = item.engines_power_hp ? ` ${item.engines_power_hp}HP` : '';
    const count = item.engines_count && item.engines_count > 1 ? `${item.engines_count}x ` : '';
    return `${count}${item.engines_brand}${hp}`;
  }
  if (item.engine_type) {
    return item.engine_type;
  }
  return '';
}

function ItemCardFields({ item, itemType: _itemType }: { item: any; itemType: ItemType }) {
  return (
    <div 
      className="flex flex-col gap-1"
      style={{ 
        fontSize: 'var(--boat-card-specs-font-size, 13px)',
        color: 'var(--color-text-secondary)'
      }}
    >
      <div>Lunghezza: {item.length_m || item.length || 'N/A'}m</div>
      <div>Larghezza: {(item.beam_m && item.beam_m > 0) ? item.beam_m + 'm' : (item.width && item.width > 0) ? item.width + 'm' : 'N/A'}</div>
      {(item.cabins_count && item.cabins_count > 0) && <div>Cabine: {item.cabins_count}</div>}
    </div>
  );
}

function getItemLink(itemType: ItemType, itemId: number | string): string {
  const id = itemId || 'MISSING-ID';
  switch (itemType) {
    case 'boats': return `/boats/${id}`;
    case 'motors': return `/motors/${id}`;
    case 'accessories': return `/accessories/${id}`;
    default: return `/${itemType}/${id}`;
  }
}

export default function ItemCard({ 
  item, 
  itemType, 
  compact = false, 
  gridMode = false, 
  distance: _distance,
  priority = false,
  canReserve: _canReserve,
  canChat: _canChat,
  canMakeOffer: _canMakeOffer
}: ItemCardProps) {
  // @ts-expect-error unused
  const _config = getItemConfig(itemType);
  const { glass: _glass, isGlassEnabled } = useGlassmorphism();
  // @ts-expect-error unused
  const { card: cardVars, boatCard: boatVars, colors } = useThemeStyled();
  const ItemIcon = getItemIcon(itemType);
  const link = getItemLink(itemType, item.id);
  const isAuthenticated = useIsAuthenticated();
  const isPublic = !isAuthenticated;
  
  const mainImage = item?.images?.[0] || null;
  
  // Check public price setting
  const settings = getPermissionSettings();
  const publicCanSeeDealerPrice = settings.public_can_view_dealer_price ?? false;
  
  // Show dealer_price to public only if setting enabled
  const showDealerPriceToPublic = isAuthenticated || publicCanSeeDealerPrice;

    // Grid mode - compact card for multi-column layouts
    if (gridMode) {
        const profitPercentage = isPublic && showDealerPriceToPublic ? calculateProfitPercentage(item.dealer_price, item.retail_price) : null;
        const categoryLabel = item.category ? item.category.toUpperCase() : (itemType === 'boats' ? 'BARCA' : itemType === 'motors' ? 'MOTORE' : 'ACCESS.');
        
        // Per utente pubblico: mostra solo prezzo retail (a meno che public_can_view_dealer_price non sia attivo)
        const displayPrice = showDealerPriceToPublic ? item.dealer_price : (item.retail_price || item.dealer_price);
        const showOriginalPrice = showDealerPriceToPublic ? (item.retail_price && item.retail_price !== item.dealer_price) : false;
        
        return (
            <Link
                to={link}
                data-testid="item-card"
onClick={(e) => {
                   if (item.status && item.status !== '' && (item.status === 'sold' || item.status === 'reserved')) {
                     e.preventDefault();
                     e.stopPropagation();
                   }
                 }}
                 className={`block glass-d-card overflow-hidden ${isGlassEnabled ? _glass.card : ''} ${(item.status === 'sold' || item.status === 'reserved') ? 'cursor-not-allowed' : ''}`}
                style={isGlassEnabled ? {
                    backgroundColor: 'var(--glass-card-bg)',
                    backdropFilter: 'blur(var(--glass-card-blur, 10px))',
                    WebkitBackdropFilter: 'blur(var(--glass-card-blur, 10px))',
                    borderRadius: 'var(--glass-card-border-radius, 16px)',
                    border: 'var(--glass-card-border)',
                    boxShadow: 'var(--glass-card-shadow)',
                } : undefined}
            >
        {/* Image - 4:3 aspect ratio for grid */}
        <div 
          className="relative bg-[var(--ios-bg-system)] overflow-hidden"
          style={{ 
            aspectRatio: 'var(--boat-card-image-aspect-ratio, 4/3)',
            minHeight: 200,
            borderRadius: 'var(--boat-card-image-border-radius, 8px)',
          }}
        >
          {mainImage ? (
            <OptimizedImage
              src={fixImageUrl(mainImage)}
              alt={item.title}
              className="w-full h-full object-contain select-none"
              width={400}
              height={300}
              sizes="(max-width: 768px) calc((100vw - 36px) / 2), 300px"
              eager={priority}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center" style={{ backgroundColor: 'var(--ios-bg-system, var(--color-surface))' }}>
              <ItemIcon className="h-12 w-12" style={{ color: 'var(--ios-light-gray-2, var(--color-text-muted))' }} />
            </div>
          )}
          
          {/* Distance Badge */}
{_distance !== undefined && _distance >= 0 && (
             <div className="absolute left-1.5 bottom-1.5 z-10">
              <div 
                className="h-5 flex items-center justify-center text-white text-[10px] font-medium px-1.5"
                style={{
                backgroundColor: getDistanceBadgeColor(_distance),
                borderRadius: '6px',
                }}
              >
                <Navigation className="w-2.5 h-2.5 mr-0.5" />
                { _distance }km
              </div>
            </div>
          )}
          
{/* Status badge - text-based, positioned left */}
        {item.status && item.status !== '' && (
          <div 
            className="absolute top-1.5 left-1.5 px-2 py-0.5 text-[8px] font-bold text-white uppercase z-10 shadow-lg"
            style={{
              backgroundColor: item.status === 'sold' ? '#dc2626' : item.status === 'reserved' ? '#f59e0b' : '#10b981',
              minWidth: '50px',
              textAlign: 'center',
              borderRadius: '6px',
            }}
          >
            {item.status === 'sold' ? 'VENDUTO' : item.status === 'reserved' ? 'PRENOTATO' : 'DISPONIBILE'}
          </div>
        )}
        </div>

        {/* Content - Compact with more info */}
           <div className="p-2 min-w-0">
<h3 className="font-semibold truncate uppercase leading-tight" style={{ fontSize: 'var(--boat-card-title-font-size, 10px)', fontWeight: 'var(--boat-card-title-font-weight, 600)', color: 'var(--card-title-color, var(--color-primary))', fontFamily: 'var(--font-family, Inter, sans-serif)' }}>
                {item.title}
              </h3>
          
          {/* Tipo barca badge + Anno + Optionals */}
          <div className="flex items-center gap-1 mt-1 flex-wrap">
            {(item.boat_type_code || item.boat_type) && (
              <span className="font-medium px-1.5 py-0.5" style={{ fontSize: 'var(--boat-card-category-font-size, 9px)', fontWeight: 'var(--boat-card-category-font-weight, 600)', color: 'var(--boat-card-category-color, var(--color-primary))', backgroundColor: 'var(--boat-card-category-background)', fontFamily: 'var(--font-family, Inter, sans-serif)', borderRadius: '4px' }}>
                {formatBoatType(item.boat_type_code, item.boat_type)}
              </span>
            )}
            {(item.accessory_type_code || item.accessory_type) && (
              <span className="font-medium px-1.5 py-0.5" style={{ fontSize: 'var(--boat-card-category-font-size, 9px)', fontWeight: 'var(--boat-card-category-font-weight, 600)', color: 'var(--boat-card-category-color, var(--color-primary))', backgroundColor: 'var(--boat-card-category-background)', fontFamily: 'var(--font-family, Inter, sans-serif)', borderRadius: '4px' }}>
                {formatAccessoryType(item.accessory_type_code, item.accessory_type)}
              </span>
            )}
            {item.year && (
              <span style={{ fontSize: 'var(--boat-card-year-font-size, 9px)', color: 'var(--boat-card-year-color, var(--color-text-secondary))', fontFamily: 'var(--font-family, Inter, sans-serif)' }}>{item.year}</span>
            )}
            {item.optional_features?.length > 0 && (
              <span className="font-bold px-1.5 py-0.5" style={{ fontSize: 'var(--boat-card-optionals-font-size, 9px)', fontWeight: 'var(--boat-card-optionals-font-weight, 600)', color: 'var(--boat-card-optionals-color, var(--color-success))', backgroundColor: 'var(--boat-card-optionals-background)', fontFamily: 'var(--font-family, Inter, sans-serif)', borderRadius: '4px' }}>
                <Star className="w-2.5 h-2.5 inline-block mr-0.5 align-text-bottom fill-current" />
                +{item.optional_features.length} optionals
              </span>
            )}
          </div>
          
          {/* Dealer / Brand + Model */}
          <div className="flex items-center gap-1.5 mt-1">
{item.dealer_logo ? (
                <OptimizedImage 
                  src={fixImageUrl(item.dealer_logo)} 
                  alt="" 
                  className="w-10 h-10 object-contain rounded"
                  width={40}
                  height={40}
                  sizes="40px"
                />
              ) : null}
            <p className="truncate flex-1" style={{ fontSize: 'var(--boat-card-dealer-font-size, 10px)', color: 'var(--boat-card-dealer-color, var(--color-text-muted))', fontFamily: 'var(--font-family, Inter, sans-serif)' }}>
              {item.dealer_name || `${item.brand} ${item.model}`}
            </p>
          </div>
          
          {/* Quick specs row - Lunghezza | Larghezza | Anno | Ospiti | Motore */}
          <div className="flex items-center gap-1.5 mt-1.5 flex-wrap" style={{ fontSize: 'var(--boat-card-specs-font-size, 10px)', color: 'var(--boat-card-specs-color, var(--color-text-muted))', fontFamily: 'var(--font-family, Inter, sans-serif)' }}>
            {item.length_m && (
              <span className="flex items-center gap-0.5 whitespace-nowrap">
                <Ruler className="w-3 h-3" />
                {item.length_m}m
              </span>
            )}
            {(item.beam_m || item.width) && (
              <span className="flex items-center gap-0.5 whitespace-nowrap">
                <RulerLucideIcon className="w-3 h-3 rotate-90" />
                {(item.beam_m && item.beam_m > 0) ? item.beam_m : item.width}m
              </span>
            )}
            {item.year && (
              <span className="flex items-center gap-0.5 whitespace-nowrap">
                <Calendar className="w-3 h-3" />
                {item.year}
              </span>
            )}
            {item.guests_max && (
              <span className="flex items-center gap-0.5 whitespace-nowrap">
                <Users className="w-3 h-3" />
                {item.guests_max}
              </span>
            )}
            {formatEngineInfo(item) && (
              <span className="truncate min-w-0 max-w-full" title={formatEngineInfo(item)} style={{ fontSize: 'var(--boat-card-engine-font-size, 10px)', color: 'var(--boat-card-engine-color, var(--color-text-muted))', fontFamily: 'var(--font-family, Inter, sans-serif)' }}>
                <Zap className="w-3 h-3 inline-block mr-0.5 align-text-bottom" />
                {formatEngineInfo(item)}
              </span>
            )}
          </div>
          
          <div className="flex items-center justify-between mt-1.5 min-w-0 gap-2">
            <div className="flex flex-col min-w-0 flex-shrink overflow-hidden">
              <span className="font-semibold truncate" style={{ fontSize: 'var(--boat-card-price-font-size, 15px)', fontWeight: 'var(--boat-card-price-font-weight, 600)', color: 'var(--boat-card-price-color, var(--color-primary))', fontFamily: 'var(--font-family, Inter, sans-serif)' }}>
                {formatPrice(displayPrice)}
              </span>
              {showOriginalPrice && (
                <span className="line-through truncate" style={{ fontSize: 'var(--boat-card-retail-price-font-size, 12px)', color: 'var(--boat-card-retail-price-color, var(--color-text-muted))', fontFamily: 'var(--font-family, Inter, sans-serif)' }}>
                  {formatPrice(item.retail_price)}
                </span>
              )}
            </div>
            <div className="flex flex-col items-end flex-shrink-0 ml-2">
              {profitPercentage !== null && (
                <span className="font-bold px-1" style={{ fontSize: 'var(--boat-card-percentage-font-size, 10px)', fontWeight: 'var(--boat-card-percentage-font-weight, 500)', color: 'var(--boat-card-percentage-color, var(--color-success))', backgroundColor: 'var(--boat-card-percentage-background)', fontFamily: 'var(--font-family, Inter, sans-serif)', borderRadius: '4px' }}>
                  +{profitPercentage}%
                </span>
              )}
              <span style={{ fontSize: 'var(--boat-card-category-font-size, 8px)', color: 'var(--boat-card-category-color, var(--color-text-muted))' }}>
                {categoryLabel}
              </span>
            </div>
          </div>

            {itemType === 'boats' && (
              <MarketPrice boatId={item.id} price={item.dealer_price || item.retail_price || undefined} compact />
            )}
        </div>
      </Link>
    );
  }

if (compact) {
     return (
       <Link
         to={link}
         data-testid="item-card"
         onClick={(e) => {
           if (item.status === 'sold' || item.status === 'reserved') {
             e.preventDefault();
             e.stopPropagation();
           }
         }}
         className={`block active:bg-[var(--ios-bg-system)] rounded-xl overflow-hidden ${item.status && item.status !== '' && (item.status === 'sold' || item.status === 'reserved') ? 'cursor-not-allowed' : ''}`}
style={{ background: 'var(--glass-card-bg, #ffffff)', backdropFilter: 'blur(8px)', border: '1px solid var(--glass-card-border, rgba(34, 42, 53, 0.08))' }}
      >
        <div className="flex">
          <div className="w-20 h-20 flex-shrink-0 rounded-lg overflow-hidden">
            {mainImage ? (
              <OptimizedImage
                src={fixImageUrl(mainImage)}
                alt={item.title}
                className="w-full h-full object-cover select-none"
                width={80}
                height={80}
                sizes="80px"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center" style={{ backgroundColor: 'var(--ios-bg-system, var(--color-surface))' }}>
                <ItemIcon className="h-8 w-8" style={{ color: 'var(--ios-light-gray-2, var(--color-text-muted))' }} />
              </div>
            )}
          </div>

          <div className="flex-1 px-3 py-1 min-w-0">
            <h3 className="font-semibold text-[15px] truncate" style={{ color: 'var(--card-title-color, var(--color-text))' }}>
              {item.title}
            </h3>
            <p className="text-[12px] mt-0.5" style={{ color: 'var(--color-text-muted)' }}>
              {item.brand} {item.model}
            </p>
            
            <div className="flex items-center justify-between mt-1">
              <div className="flex flex-col">
                <span className="text-[15px] font-semibold" style={{ color: 'var(--color-primary)' }}>
                  {formatPrice(isPublic ? (item.retail_price || item.dealer_price) : item.dealer_price)}
                </span>
                {(!isPublic && item.retail_price && item.retail_price !== item.dealer_price) && (
                  <span className="text-[10px] line-through" style={{ color: 'var(--color-text-muted)' }}>
                    {formatPrice(item.retail_price)}
                  </span>
                )}
              </div>
{_distance !== undefined && _distance >= 0 && (
                <span 
                  className="text-[12px] text-white px-2 py-1"
                  style={{ backgroundColor: getDistanceBadgeColor(_distance), borderRadius: '6px' }}
                >
                { _distance }km
                </span>
              )}
            </div>

            {itemType === 'boats' && (
              <MarketPrice boatId={item.id} price={item.dealer_price || item.retail_price || undefined} compact />
            )}
          </div>
        </div>
      </Link>
    );
  }

// Full card - Native iOS style
   return (
     <Link
       to={link}
       data-testid="item-card"
       onClick={(e) => {
         if (item.status === 'sold' || item.status === 'reserved') {
           e.preventDefault();
           e.stopPropagation();
         }
       }}
       className={`block active:bg-[var(--ios-bg-system)] rounded-xl overflow-hidden ${item.status === 'sold' || item.status === 'reserved' ? 'cursor-not-allowed' : ''}`}
      style={{ background: 'var(--glass-card-bg, #ffffff)', backdropFilter: 'blur(8px)', border: '1px solid var(--glass-card-border, rgba(34, 42, 53, 0.08))' }}
    >
      {/* Image - Square aspect ratio */}
      <div className="relative aspect-square overflow-hidden" style={{ backgroundColor: 'var(--ios-light-gray-3, var(--color-border))' }}>
          {mainImage ? (
            <OptimizedImage
              src={fixImageUrl(mainImage)}
              alt={item.title}
              className="w-full h-full object-contain select-none"
              width={400}
              height={400}
              sizes="(max-width: 768px) 100vw, 400px"
            />
          ) : (
          <div className="w-full h-full flex items-center justify-center">
            <ItemIcon className="h-16 w-16" style={{ color: 'var(--ios-light-gray-2, var(--color-text-muted))' }} />
          </div>
        )}
        
        {/* Distance Badge */}
{_distance !== undefined && _distance >= 0 && (
            <div className="absolute left-2 bottom-2 z-10">
             <div 
               className="h-6 flex items-center justify-center text-white text-[11px] font-medium px-2"
               style={{
                 backgroundColor: getDistanceBadgeColor(_distance),
                 borderRadius: '6px',
               }}
             >
               <Navigation className="w-3 h-3 mr-0.5" />
               { _distance }km
             </div>
           </div>
         )}

         {/* Item Type Badge */}
         <div className="absolute left-2 top-2 z-10">
           <div 
             className="h-6 flex items-center justify-center text-white text-[9px] font-bold px-1.5"
             style={{
               backgroundColor: itemType === 'boats' ? 'var(--color-primary)' :
                 itemType === 'motors' ? 'var(--color-secondary)' :
                 'var(--color-accent)',
               borderRadius: '6px',
             }}
           >
             <ItemIcon className="w-2.5 h-2.5 mr-0.5" />
             {itemType === 'boats' ? 'BARCA' :
              itemType === 'motors' ? 'MOTORE' :
              'ACCESS.'}
           </div>
         </div>
         
         {/* Status badge */}
         {item.status && item.status !== '' && (
           <div 
             className="absolute top-2 right-2 px-3 py-1 text-[10px] font-bold text-white uppercase z-10 shadow-lg"
             style={{
               backgroundColor: item.status === 'sold' ? '#dc2626' : item.status === 'reserved' ? '#f59e0b' : '#10b981',
               minWidth: '70px',
               textAlign: 'center',
               borderRadius: '6px',
             }}
           >
             {item.status === 'sold' ? 'VENDUTO' : item.status === 'reserved' ? 'PRENOTATO' : 'DISPONIBILE'}
           </div>
         )}
      </div>

      {/* Content */}
      <div className="p-3">
        <h3 className="font-semibold text-[15px] truncate" style={{ color: 'var(--card-title-color, var(--color-text))' }} data-testid="item-title">
          {item.title}
        </h3>
        <p className="text-[13px] mt-0.5" style={{ color: 'var(--color-text-secondary)' }}>
          {item.brand} {item.model}
        </p>

        {/* Compact fields */}
        <div className="mt-2">
          <ItemCardFields item={item} itemType={itemType} />
        </div>

        {/* Location - iOS style */}
        {item.location && (
          <div className="mt-2 text-[12px] flex items-center gap-1" style={{ color: 'var(--color-text-muted)' }}>
            <span className="truncate">{item.location}</span>
          </div>
        )}

        {/* Dealer - iOS style */}
        {item.dealer_name && (
          <div className="mt-1 text-[12px] flex items-center gap-1" style={{ color: 'var(--color-text-muted)' }}>
            <span className="truncate">{item.dealer_name}</span>
          </div>
        )}

        {/* Price */}
        <div className="flex items-center justify-between mt-3">
          <span className="text-[17px] font-semibold" style={{ color: 'var(--color-primary)' }} data-testid="item-price">
            {formatPrice(isPublic ? (item.retail_price || item.dealer_price) : item.dealer_price)}
          </span>
          <ChevronRight className="w-5 h-5" style={{ color: 'var(--color-text-muted)' }} />
        </div>

        {itemType === 'boats' && (
          <MarketPrice boatId={item.id} price={item.dealer_price || item.retail_price || undefined} compact />
        )}
      </div>
    </Link>
  );
}